import { useEffect, useState } from "react";
import {
  Box,
  Divider,
  Drawer,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CreateOutlinedIcon from "@mui/icons-material/CreateOutlined";
import ConversationList from "./ConversationList";
import ChatThread from "./ChatThread";
import NewChatDialog from "./NewChatDialog";
import { useMessenger } from "./MessengerContext";

const DRAWER_WIDTH = 720;

export default function GlobalMessengerDrawer() {
  const theme = useTheme();
  const isSmUp = useMediaQuery(theme.breakpoints.up("sm"));
  const [open, setOpen] = useState(false);
  const [newChatOpen, setNewChatOpen] = useState(false);
  const { mobileShowThread, setMobileShowThread, activeConversationId, setDrawerViewState, refreshConversations } =
    useMessenger();

  useEffect(() => {
    const onToggle = () => setOpen((prev) => !prev);
    const onOpen = () => setOpen(true);
    window.addEventListener("rcm-messenger-drawer-toggle", onToggle);
    window.addEventListener("rcm-messenger-drawer-open", onOpen);
    return () => {
      window.removeEventListener("rcm-messenger-drawer-toggle", onToggle);
      window.removeEventListener("rcm-messenger-drawer-open", onOpen);
    };
  }, []);

  const showList = isSmUp || !mobileShowThread;
  const showThread = isSmUp || mobileShowThread;
  const threadVisible =
    open && (isSmUp ? Boolean(activeConversationId) : mobileShowThread);

  useEffect(() => {
    setDrawerViewState({ drawerOpen: open, threadVisible });
  }, [open, threadVisible, setDrawerViewState]);

  useEffect(() => {
    if (open) void refreshConversations();
  }, [open, refreshConversations]);

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        keepMounted
        sx={{
          zIndex: (t) => t.zIndex.drawer + 2,
          "& .MuiDrawer-paper": {
            width: isSmUp ? DRAWER_WIDTH : "100%",
            maxWidth: "100vw",
            height: "100%",
            overflow: "hidden",
            boxSizing: "border-box",
            bgcolor: "background.default",
          },
        }}
      >
        <Box className="flex flex-col h-full">
          <Box
            className="flex items-center justify-between px-3 py-2"
            sx={{
              bgcolor: "background.paper",
              color: "text.primary",
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontSize: "1.05rem",
                fontWeight: 600,
                color: "text.primary",
                bgcolor: "transparent",
              }}
            >
              User Chat
            </Typography>
            <Box className="flex items-center">
              <IconButton
                size="small"
                aria-label="New message"
                onClick={() => setNewChatOpen(true)}
                color="primary"
              >
                <CreateOutlinedIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                aria-label="Close messenger"
                onClick={() => setOpen(false)}
                color="primary"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
          <Divider />
          <Box className="flex flex-1 min-h-0">
            {showList && (
              <Box
                sx={{
                  width: isSmUp ? 280 : "100%",
                  borderRight: isSmUp ? 1 : 0,
                  borderColor: "divider",
                  minHeight: 0,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  bgcolor: "background.paper",
                }}
              >
                <ConversationList />
              </Box>
            )}
            {showThread && (
              <Box className="flex-1 min-w-0 min-h-0 h-full">
                <ChatThread
                  showBack={!isSmUp}
                  onBack={() => {
                    setMobileShowThread(false);
                  }}
                />
              </Box>
            )}
          </Box>
        </Box>
      </Drawer>
      <NewChatDialog open={newChatOpen} onClose={() => setNewChatOpen(false)} />
    </>
  );
}
